<template>
  <div class="d-flex flex-column h-100">
    <h2
      v-b-toggle.quickdrop
      class="mb-0 d-flex align-items-center justify-content-between"
    >
      Quick Drop
      <i class="icon-arrow-down font-xl" />
    </h2>

    <b-collapse id="quickdrop" visible>
      <div class="my-2">
        Drag and drop files here to quickly open them in Nanome next time you
        put on your headset.
      </div>
      <div class="mb-3 small">
        <b>Files are securely stored for up to 24 hours</b>
        before being automatically erased. Files will be deleted from Quick Drop
        once loaded into Nanome.
      </div>

      <div
        :class="{ 'quickdrop--dragging': isDragging && canAdd }"
        class="quickdrop flex-fill"
        @dragover.prevent
        @dragenter.prevent="isDragging = true"
        @dragleave.prevent.self="isDragging = false"
        @drop.prevent="onDrop"
        @click="isDragging = false"
      >
        <div v-if="showDropzone && canAdd" class="quickdrop__dropzone">
          <div class="text-center">
            <div class="font-weight-bold">Drag and drop files here</div>
            <div class="small">Maximum total file size is 100 MB</div>
          </div>
        </div>
        <div v-else-if="showDropzone && !canAdd" class="quickdrop__dropzone">
          <div class="text-center">
            <div class="font-weight-bold">You do not have permission</div>
            <div class="small">to add files to this user's Quick Drop</div>
          </div>
        </div>
        <div v-if="files.length && !uploads.length" class="quickdrop__overlay">
          <div class="text-center">
            <div class="font-weight-bold">Files are ready, open Nanome now</div>
            <div class="small">or drag and drop to upload more files</div>
          </div>
        </div>
        <ul
          v-if="numItems > 0"
          class="quickdrop__files w-100 flex-fill m-0 list-unstyled"
        >
          <li
            v-for="file of files"
            :key="`file-${file.filename}`"
            class="p-2 d-flex align-items-center"
          >
            <i class="icon-doc mr-1" />
            <div class="quickdrop__filename">{{ file.filename }}</div>
            <div class="small text-muted">
              {{ file.size | bytes }}
            </div>
            <b-btn
              v-if="!isAdmin || hasPermission('quickdrop.delete')"
              v-b-tooltip.hover="'Delete file'"
              class="py-0 px-2"
              variant="link"
              @click="deleteItem(file.filename)"
            >
              <i class="icon-trash text-danger" />
            </b-btn>
          </li>
          <li
            v-for="upload of uploads"
            :key="`upload-${upload.filename}`"
            class="p-2 d-flex align-items-center"
          >
            <i class="icon-cloud-upload mr-1" />
            <div class="quickdrop__filename">{{ upload.filename }}</div>
            <div class="small text-muted">
              {{ Math.floor(100 * upload.progress) }}%
            </div>
            <b-btn
              v-b-tooltip.hover="'Cancel upload'"
              class="py-0 px-2"
              variant="link"
              @click="cancelUpload(upload)"
            >
              <i class="icon-close text-danger" />
            </b-btn>
          </li>
        </ul>
      </div>

      <div class="mt-2 d-flex justify-content-between">
        <div class="font-xs text-nowrap text-muted">
          {{ numItems | pluralize("item") }} <br />
          {{ totalSize | bytes }}
        </div>

        <div class="text-right">
          <b-dropdown v-if="canAdd" class="mr-2" variant="primary">
            <template #button-content>
              <i class="icon-cloud-upload mr-1" /> Upload Files
            </template>
            <b-dropdown-item @click="openFileInput">
              <i class="icon-screen-desktop mr-1 text-dark" /> From Computer
            </b-dropdown-item>
            <b-dropdown-item v-b-modal.smiles-input>
              <i class="icon-emotsmile mr-1 text-dark" /> From SMILES
            </b-dropdown-item>
          </b-dropdown>

          <b-btn
            v-if="!isAdmin || hasPermission('quickdrop.delete')"
            :disabled="!files.length"
            variant="danger"
            @click="reset"
          >
            <i class="icon-trash mr-1" /> Delete All
          </b-btn>
        </div>
      </div>

      <input ref="fileInput" type="file" class="d-none" @change="onFileInput" />
    </b-collapse>

    <b-modal ref="modal" id="smiles-input" centered hide-header hide-footer>
      <h4 class="mb-3">Upload SMILES</h4>
      <textarea
        v-model="smiles"
        class="w-100 form-control"
        rows="10"
        placeholder="Paste SMILES here"
      />
      <div class="mt-3 text-right">
        <b-btn
          variant="secondary"
          class="px-4 mr-2"
          @click="$refs.modal.hide()"
        >
          Cancel
        </b-btn>
        <b-btn
          :disabled="!smiles"
          variant="primary"
          class="px-4"
          @click="uploadSmiles"
        >
          Upload
        </b-btn>
      </div>
    </b-modal>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import API from "@/api";
import { getFiles } from "@/helpers/files";

const MB = 1024 ** 2;

export default {
  data: () => ({
    isDragging: false,
    updateTID: null,
    smiles: "",
    files: [],
    uploads: []
  }),

  computed: {
    ...mapGetters(["isAdmin", "hasPermission"]),
    ...mapGetters({
      license: "user/activeLicense"
    }),

    canAdd() {
      return !this.isAdmin || this.hasPermission("quickdrop.add");
    },

    hasWorkspaceFile() {
      const fileExts = this.files.map(f => f.filename.split(".").pop());
      const uploadExts = this.uploads.map(u => u.filename.split(".").pop());
      const allExts = [...fileExts, ...uploadExts];
      return allExts.includes("nanome") || allExts.includes("nanosr");
    },

    numItems() {
      return this.files.length + this.uploads.length;
    },

    showDropzone() {
      return this.isDragging || !this.numItems;
    },

    totalSize() {
      const files = this.files.reduce((acc, f) => acc + f.size, 0);
      const uploads = this.uploads.reduce((acc, u) => acc + u.size, 0);
      return files + uploads;
    }
  },

  mounted() {
    document.addEventListener("paste", this.onPaste);
    this.$once("hook:beforeDestroy", () => {
      document.removeEventListener("paste", this.onPaste);
    });

    this.refresh();
  },

  methods: {
    async refresh() {
      const { success, results } = await API.getQuickdrops();
      if (!success) return;
      this.files = results;
    },

    cancelUpload(upload) {
      if (!upload.error) upload.cancel();
      this.uploads = this.uploads.filter(u => u !== upload);
    },

    async deleteItem(filename) {
      const { success } = await API.deleteQuickdrop(filename);
      if (!success) return;
      this.refresh();
    },

    async onDrop(e) {
      if (!this.canAdd) return;

      this.isDragging = false;
      const files = await getFiles(e);
      this.addFiles(files);
    },

    async onFileInput(e) {
      await this.addFiles(e.target.files);
      this.$refs.fileInput.value = "";
    },

    async onPaste(e) {
      if (!this.canAdd) return;

      const files = await getFiles(e);
      this.addFiles(files);
    },

    openFileInput() {
      this.$refs.fileInput.click();
    },

    uploadSmiles() {
      this.$refs.modal.hide();
      let n = 1;
      let filename = "molecule 1.smi";
      while (this.files.map(f => f.filename).includes(filename)) {
        filename = `molecule ${++n}.smi`;
      }

      const file = new File([this.smiles], filename, {
        type: "text/plain"
      });
      this.addFiles([file]);
      this.smiles = "";
    },

    async addFiles(files) {
      const filenames = [];
      let skipped = 0;
      for (const file of files) {
        const filename = file.name.replace(/[\\/]/g, "-");
        if (filenames.includes(filename)) {
          skipped++;
          continue;
        }
        filenames.push(filename);
      }

      if (this.license.type === "free" && this.numItems >= 2) {
        this.$notify({
          type: "info",
          text: "Free users can only upload 2 files"
        });
        return;
      }

      if (skipped > 0) {
        this.$notify({
          type: "info",
          text: `Skipping ${skipped} duplicate file names`
        });
      }

      const r = await API.addQuickdrop(filenames);
      if (!r.success) {
        return this.$notify({
          type: "error",
          text: r.error.message
        });
      }

      const urls = r.results;
      for (const file of files) {
        const filename = file.name.replace(/[\\/]/g, "-");
        if (!filenames.includes(filename)) continue;

        if (file.size + this.totalSize > 100 * MB) {
          this.$notify({
            type: "info",
            text: `Skipping ${filename} as it would exceed the 100MB limit`
          });
          continue;
        }

        const ext = filename.split(".").pop();
        if (["nanome", "nanosr"].includes(ext) && this.hasWorkspaceFile) {
          this.$notify({
            type: "info",
            text: `Skipping ${filename} as it there is already a workspace file (.nanome or .nanosr)`
          });
          continue;
        }

        this.addUpload(filename, file, urls[filename]);
      }
    },

    addUpload(filename, file, url) {
      clearTimeout(this.updateTID);
      const request = new XMLHttpRequest();
      const cancel = () => request.abort();

      const upload = {
        filename,
        size: file.size,
        progress: 0,
        error: false,
        cancel
      };

      request.open("PUT", url);
      request.upload.addEventListener("progress", e => {
        upload.progress = e.loaded / e.total;
      });
      request.addEventListener("load", async () => {
        if (request.status === 200) {
          await this.refresh();
          this.uploads = this.uploads.filter(u => u !== upload);

          if (!this.uploads.length) {
            // give 5s grace period before sending "update" event
            this.updateTID = setTimeout(() => {
              API.updateQuickdrop();
            }, 5000);
          }
        } else {
          upload.error = true;
        }
      });
      request.send(file);

      this.uploads.push(upload);
    },

    async reset() {
      await API.resetQuickdrop();
      this.refresh();
    }
  }
};
</script>

<style lang="scss" scoped>
.quickdrop {
  position: relative;

  min-height: 13em;
  max-height: 26em;
  height: 100%;
  width: 100%;
  border: 2px dashed var(--primary);
  border-radius: 5px;

  &__files {
    overflow-y: auto;
    min-height: 6em;
    max-height: 26em;
  }

  &__dropzone,
  &__overlay {
    display: none;
    position: absolute;
    width: 100%;
    height: 100%;
    top: 0;
    left: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    pointer-events: none;
  }

  &__dropzone {
    background-color: #ecf5fe;
  }

  &__overlay > div {
    padding: 0.5em 1em;
    backdrop-filter: brightness(4) blur(10px);
    background: #8882;
    border-radius: 5px;
    z-index: 1;
  }

  &--dragging &__dropzone {
    background-color: #bddcfb;
  }

  &--dragging &__files {
    pointer-events: none;
  }

  &__filename {
    flex: 1;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
}
</style>
